import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/app/providers';
import { toast } from '@/components/feedback/toast';
import {
  useHandleAvailability,
  type HandleStatus,
} from '@/features/settings/hooks/useSettingsData';
import type { Status } from '@/features/settings/settings';
import type { Profile } from '@/types';
import { useUpdateProfile } from './useProfile';

/** The working copy of the editable public-profile fields. */
export interface ProfileEditDraft {
  avatarUrl: string;
  displayName: string;
  /** Without the leading `@` (stored WITH `@` in the DB). */
  handle: string;
  bio: string;
  status: Status;
}

export interface ProfileEditorState {
  draft: ProfileEditDraft;
  set: <K extends keyof ProfileEditDraft>(key: K, value: ProfileEditDraft[K]) => void;
  dirty: boolean;
  saving: boolean;
  handleStatus: HandleStatus;
  /** Display name is within its 14-day change cooldown — the field is locked. */
  nameLocked: boolean;
  /** Whole days until the name can be changed again (0 when unlocked). */
  nameUnlockInDays: number;
  save: () => void;
  discard: () => void;
}

const NAME_COOLDOWN_DAYS = 14;
const DAY_MS = 86_400_000;

/** Resolve the display-name cooldown from the last-changed timestamp. */
function nameCooldown(changedAt: string | null | undefined): {
  locked: boolean;
  daysLeft: number;
} {
  if (!changedAt) return { locked: false, daysLeft: 0 };
  const until = new Date(changedAt).getTime() + NAME_COOLDOWN_DAYS * DAY_MS;
  const remaining = until - Date.now();
  return remaining > 0
    ? { locked: true, daysLeft: Math.ceil(remaining / DAY_MS) }
    : { locked: false, daysLeft: 0 };
}

/** Seed the draft from the live `profiles` row. */
function seed(profile: Profile | null): ProfileEditDraft {
  return {
    avatarUrl: profile?.avatar_url ?? '',
    displayName: profile?.display_name ?? '',
    handle: (profile?.handle ?? '').replace(/^@/, ''),
    bio: profile?.bio ?? '',
    status: (profile?.status as Status) || 'online',
  };
}

/**
 * The profile page's edit state — the OG settings Profile-tab draft model,
 * relocated to the profile page and scoped to just the public-profile fields:
 * a working `draft`, a `baseline` to diff for dirty detection, live username
 * availability, and a save that blocks on an invalid/taken/blank field. Persists
 * via `useUpdateProfile`, then refreshes AuthProvider so the TopNav updates.
 */
export function useProfileEditor(profile: Profile | null): ProfileEditorState {
  const { user, refreshProfile } = useAuth();
  const mutation = useUpdateProfile();

  const [draft, setDraft] = useState<ProfileEditDraft>(() => seed(profile));
  const [baseline, setBaseline] = useState<ProfileEditDraft>(draft);
  /** The profile id the draft was seeded from (re-seed only on identity change). */
  const seededFor = useRef<string | null>(profile?.id ?? null);

  // Re-seed when the profile identity changes. Gated on id so a post-save
  // refresh (same id) never clobbers the just-saved baseline.
  useEffect(() => {
    const id = profile?.id ?? null;
    if (id === seededFor.current) return;
    seededFor.current = id;
    const fresh = seed(profile);
    setDraft(fresh);
    setBaseline(fresh);
  }, [profile]);

  const set = useCallback<ProfileEditorState['set']>((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  const handleStatus = useHandleAvailability(draft.handle, user?.id);
  const { locked: nameLocked, daysLeft: nameUnlockInDays } = nameCooldown(
    profile?.display_name_changed_at,
  );

  const discard = useCallback(() => setDraft(baseline), [baseline]);

  const save = useCallback(() => {
    if (handleStatus === 'invalid' || handleStatus === 'taken') {
      toast('Pick a valid, available username before saving', 'error');
      return;
    }
    if (draft.displayName.trim().length === 0) {
      toast("Display name can't be blank", 'error');
      return;
    }
    if (!profile) return;

    // Display name can only change once per 14 days. The field is disabled while
    // locked, so this is a defense-in-depth guard against a stale/forced edit.
    const nameChanged = draft.displayName.trim() !== baseline.displayName.trim();
    if (nameChanged && nameLocked) {
      toast(`You can change your name again in ${nameUnlockInDays} day(s)`, 'error');
      return;
    }

    mutation.mutate(
      { id: profile.id, fields: draft, stampNameChange: nameChanged },
      {
        onSuccess: () => {
          setBaseline(draft);
          void refreshProfile();
          toast('✓ Profile saved', 'ok');
        },
        onError: (err) => toast((err as Error).message || 'Save failed', 'error'),
      },
    );
  }, [
    draft,
    baseline,
    profile,
    handleStatus,
    nameLocked,
    nameUnlockInDays,
    mutation,
    refreshProfile,
  ]);

  return {
    draft,
    set,
    dirty,
    saving: mutation.isPending,
    handleStatus,
    nameLocked,
    nameUnlockInDays,
    save,
    discard,
  };
}
